import React, { useRef, useEffect, useCallback, useState } from 'react';

// Fetch API key from server (works at runtime, no build-time env needed)
let cachedKey = null;
async function getApiKey() {
  if (cachedKey !== null) return cachedKey;
  try {
    const res = await fetch('/api/config/maps-key');
    const { key } = await res.json();
    cachedKey = key || '';
    return cachedKey;
  } catch {
    cachedKey = '';
    return '';
  }
}

let scriptLoaded = false;
let scriptLoading = false;

function loadScript(apiKey) {
  return new Promise((resolve) => {
    if (window.google?.maps?.places) { resolve(true); return; }
    if (scriptLoaded) { resolve(!!window.google?.maps?.places); return; }
    if (scriptLoading) {
      const check = setInterval(() => {
        if (window.google?.maps?.places) { clearInterval(check); resolve(true); }
      }, 100);
      setTimeout(() => { clearInterval(check); resolve(false); }, 8000);
      return;
    }

    scriptLoading = true;
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => {
      scriptLoaded = true;
      scriptLoading = false;
      setTimeout(() => resolve(!!window.google?.maps?.places), 200);
    };
    script.onerror = () => { scriptLoading = false; resolve(false); };
    document.head.appendChild(script);
  });
}

export default function AddressAutocomplete({ value, onChange }) {
  const inputRef = useRef(null);
  const acRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [ready, setReady] = useState(false);

  const init = useCallback(async () => {
    if (acRef.current) return;

    const apiKey = await getApiKey();
    if (!apiKey) return;

    const loaded = await loadScript(apiKey);
    if (!loaded || !inputRef.current) return;

    try {
      const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: 'fr' },
        fields: ['formatted_address', 'geometry', 'address_components'],
        types: ['address'],
      });

      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (!place?.geometry) return;
        let ville = '', codePostal = '';
        for (const comp of (place.address_components || [])) {
          if (comp.types.includes('locality')) ville = comp.long_name;
          if (comp.types.includes('postal_code')) codePostal = comp.long_name;
        }
        onChangeRef.current({
          adresse: place.formatted_address || '',
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          ville,
          codePostal,
        });
      });

      acRef.current = ac;
      setReady(true);
    } catch (e) {
      console.warn('[AddressAutocomplete]', e.message);
    }
  }, []);

  useEffect(() => { init(); }, [init]);

  return (
    <input
      ref={inputRef}
      type="text"
      defaultValue={value || ''}
      onChange={(e) => onChangeRef.current({ adresse: e.target.value })}
      placeholder={ready ? 'Rechercher une adresse...' : 'Adresse du chantier'}
      className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg text-sm focus:border-orange-500 outline-none min-h-[44px]"
    />
  );
}
